import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { PasswordResetForm } from '../components/PasswordResetForm';

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkSession, user } = useAuth();
  const { showSuccess, showError, showInfo } = useToast();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'password-reset'>('loading');
  const [message, setMessage] = useState<string>('');
  const processedRef = useRef(false); // Use ref to prevent re-runs

  useEffect(() => {
    // Prevent multiple executions
    if (processedRef.current) {
      return;
    }
    
    const handleEmailVerification = async () => {
      try {
        processedRef.current = true; // Mark as processed immediately
        
        // Get verification parameters from URL
        const token = searchParams.get('token');
        const type = searchParams.get('type');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        // 🔍 Special handling for already logged in users clicking confirmation links
        if (user && type === 'signup') {
          showInfo('Your account is already activated and you are logged in!');
          setTimeout(() => {
            navigate('/');
          }, 2000);
          return;
        }

        // Check for errors first
        if (error) {
          console.error('Email verification error:', error, errorDescription);
          setStatus('error');
          setMessage(errorDescription || error);
          showError(errorDescription || 'Email verification failed');
          return;
        }

        if (!token || !type) {
          throw new Error('Missing verification parameters');
        }

        // Verify the email confirmation with Supabase
        const API_BASE = import.meta.env.VITE_SUPABASE_URL || 'http://localhost:8000';
        const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

        const response = await fetch(`${API_BASE}/auth/v1/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': ANON_KEY
          },
          body: JSON.stringify({
            token,
            type,
          })
        });

        if (response.ok) {
          const data = await response.json();
          
          // If we get session data back, store it and log the user in
          if (data.access_token && data.refresh_token) {
            const session = {
              access_token: data.access_token,
              refresh_token: data.refresh_token,
              expires_at: Date.now() + ((data.expires_in || 3600) * 1000)
            };
            
            // Default to localStorage for email confirmations (user likely wants to stay logged in)
            localStorage.setItem('cycling-buddy-session', JSON.stringify(session));
            
            // Trigger auth context update
            await checkSession();
          }
          
          setStatus('success');
          let successMessage = 'Email verified successfully!';
          
          if (type === 'signup') {
            successMessage = 'Your account has been activated! Welcome to CyclingBuddy!';
          } else if (type === 'email_change') {
            successMessage = 'Email change confirmed successfully!';
          } else if (type === 'recovery') {
            // For password recovery, show the password reset form instead of success message
            setStatus('password-reset');
            return; // Exit early, don't show success message or redirect
          }
          
          setMessage(successMessage);
          showSuccess(successMessage);
          
          // Redirect after a short delay
          setTimeout(() => {
            navigate('/');
          }, 3000);
          
        } else {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.message || errorData.msg || errorData.error_description || '';
          
          // Handle specific error cases based on type and error message
          if (type === 'signup') {
            
            // Check if this is an already confirmed account - attempt to use token for login
            if (errorMessage.toLowerCase().includes('already confirmed') || 
                errorMessage.toLowerCase().includes('already verified')) {
                            
              // Try to use the verification token to get session data
              try {
                const sessionResponse = await fetch(`${API_BASE}/auth/v1/token?grant_type=refresh_token`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'apikey': ANON_KEY
                  },
                  body: JSON.stringify({
                    refresh_token: token // Try using the token as a refresh token
                  })
                });
                
                if (sessionResponse.ok) {
                  const sessionData = await sessionResponse.json();
                  if (sessionData.access_token) {
                    const session = {
                      access_token: sessionData.access_token,
                      refresh_token: sessionData.refresh_token,
                      expires_at: Date.now() + ((sessionData.expires_in || 3600) * 1000)
                    };
                    
                    localStorage.setItem('cycling-buddy-session', JSON.stringify(session));
                    await checkSession();
                    
                    setStatus('success');
                    setMessage('Welcome back! Your account is already activated and you have been signed in automatically.');
                    showSuccess('Account already activated! You have been signed in automatically.');
                    
                    setTimeout(() => {
                      navigate('/');
                    }, 2000);
                    return;
                  }
                }
              } catch (tokenError) {
                console.warn('⚠️ Could not use token for automatic sign-in:', tokenError);
              }
              
              // If automatic sign-in didn't work, show success message and redirect
              setStatus('success');
              setMessage('Your account is already activated! Redirecting you to sign in...');
              showSuccess('Account already activated! Please sign in to continue.');
              
              setTimeout(() => {
                navigate('/');
              }, 3000);
              return;
            }
            
            // Check if token is expired or invalid
            if (errorMessage.toLowerCase().includes('token expired') ||
                errorMessage.toLowerCase().includes('token invalid') ||
                errorMessage.toLowerCase().includes('invalid token') ||
                response.status === 401 || response.status === 422) {
              
              setStatus('error');
              setMessage('This confirmation link has expired. Your account may already be active - try signing in, or request a new confirmation email if needed.');
              
              // Show error and redirect to home where they can sign in
              showError('This confirmation link has expired. Please try signing in to your account.');
              setTimeout(() => {
                navigate('/');
              }, 5000);
              return;
            } else {
              console.warn('❌ Signup error did not match already-confirmed patterns');
            }
          } else if (type === 'recovery') {
            console.warn('🔐 Processing password recovery error...');
            // For password recovery errors, show password reset form
            if (errorMessage.toLowerCase().includes('token expired') || 
                errorMessage.toLowerCase().includes('token invalid') ||
                errorMessage.toLowerCase().includes('invalid token') ||
                response.status === 401 || response.status === 422) {
              
              setStatus('password-reset');
              return;
            }
          }
          
          // Generic error fallback
          throw new Error(errorMessage || `Verification failed: ${response.status}`);
        }
      } catch (err) {
        console.error('Email verification error:', err);
        setStatus('error');
        const errorMsg = err instanceof Error ? err.message : 'Email verification failed';
        setMessage(errorMsg);
        showError(errorMsg);
      }
    };

    handleEmailVerification();
  }, [navigate, checkSession, searchParams, user]); // Added user to dependencies

  const handleReturnHome = () => {
    navigate('/');
  };

  const handlePasswordResetSuccess = () => {
    navigate('/');
  };

  const handlePasswordResetCancel = () => {
    navigate('/');
  };

  if (status === 'loading') {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="50vh"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="h6">
          Verifying your email...
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please wait while we confirm your email address.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="50vh"
      gap={2}
      sx={{ px: 2 }}
    >
      {status === 'password-reset' ? (
        <PasswordResetForm 
          onSuccess={handlePasswordResetSuccess}
          onCancel={handlePasswordResetCancel}
        />
      ) : status === 'success' ? (
        <>
          <CheckCircle color="success" sx={{ fontSize: 48 }} />
          <Typography variant="h5" textAlign="center" color="success.main">
            Email Verified!
          </Typography>
          <Typography variant="body1" textAlign="center" sx={{ maxWidth: 400 }}>
            {message}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            You will be redirected to the home page in a few seconds...
          </Typography>
          <Button variant="outlined" onClick={handleReturnHome} sx={{ mt: 2 }}>
            Go to Home Page Now
          </Button>
        </>
      ) : (
        <>
          {message.includes('already been activated') || message.includes('already activated') ? (
            // Special case for already activated accounts
            <>
              <CheckCircle color="warning" sx={{ fontSize: 48 }} />
              <Typography variant="h5" textAlign="center" color="warning.main">
                Account Already Activated
              </Typography>
              <Alert severity="info" sx={{ maxWidth: 500, textAlign: 'center' }}>
                {message}
              </Alert>
              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 400 }}>
                Your account is ready to use. You can sign in anytime using your email and password.
              </Typography>
              <Button variant="contained" onClick={handleReturnHome} sx={{ mt: 2 }}>
                Go to Home Page
              </Button>
            </>
          ) : (
            // Generic error case
            <>
              <ErrorIcon color="error" sx={{ fontSize: 48 }} />
              <Typography variant="h5" textAlign="center" color="error.main">
                Verification Failed
              </Typography>
              <Alert severity="error" sx={{ maxWidth: 400 }}>
                {message}
              </Alert>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Please try again or contact support if the problem persists.
              </Typography>
              <Button variant="contained" onClick={handleReturnHome} sx={{ mt: 2 }}>
                Return to Home Page
              </Button>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default EmailVerificationPage;
